import { IGatsbyImageData } from 'gatsby-plugin-image'
import { formatContractType, formatStartDate } from '../../offers'
import {
  addDomainOnUrl,
  returnNullIfEmptyString,
} from '../../../Utils/functionnals'
import { formatMediaFromGraphqlRequest } from '../../ui/medias/utils'
import { FirmCareerSite } from '../../../types/firm/careerSite'
import { careerSiteInfos } from '../../../Utils/firm/careerSite'
import { Subscription } from '../../../types/firm/Subscription'
import { ContractType } from '../../../types/ContractType'

export type Firm = {
  id: number | null
  uuid: string | null
  name: string
  slug: string | null
  totalEmployees: string | null
  foundedYear: number | null
  employeeAgeAverage: number | null
  totalClients: number | null
  colorCareerSite: string | null
  hasCareerSite: boolean | null
  careerSite?: FirmCareerSite
  premium: boolean | null
  website: string | null
  description: string | null
  introduction: string | null
  firmValues: string | null
  clientDescription: string | null
  logo: string | IGatsbyImageData | null
  logoUrl: string | null
  header: string | IGatsbyImageData | null
  headerUrl: string | null
  nbOffices: number | null
  nbOffers: number | null
  mainMedia: mediaType | null
  badges: Badge[] | null
  ecoSystems: EcoSystem[] | null
  jobTypes: JobType[] | null
  socials: Socials
  medias: mediaType[] | null
  tools: Tool[] | null
  offices: Office[] | null
  offers: FirmOffer[] | null
  teams: Team[] | null
  subscription: Subscription | null
}

export type EcoSystem = {
  name: string | null
  logo: string | IGatsbyImageData | null
  logoUrl: string | null
} | null

export type JobType = {
  value: string | null
} | null

export type Tool = {
  name: string | null
  logo: string | IGatsbyImageData | null
  logoUrl: string | null
  slug: string | null
} | null

export type Badge = {
  name: string
  uuid: string
  description?: string | null
} | null

export type Office = {
  uuid: string | null
  name: string | null
  street: string | null
  postalCode: string | null
  city: string | null
  cityForMap: string | null
  latitude: number | null
  longitude: number | null
} | null

export type Socials = {
  facebook: string | null
  linkedin: string | null
  twitter: string | null
  youtube: string | null
  instagram: string | null
} | null

export type Team = {
  name: string | null
  foundedYear: number | null
  totalEmployees: string | null
  employeeAgeAverage: number | null
  description: string | null
  socials: Socials
  mainMedia: mediaType | null
  medias: mediaType[] | null
} | null

export type FirmOffer = {
  title: string
  slug: string
  publishedAt: Date | string | null
  jobType: JobType
  payRange: {
    min: number | null
    max: number | null
  } | null
  contractType: ContractType | null
  startDate: string | null
  office: {
    cityForMap: string | null
    city: string | null
    localisation: string | null
    street: string | null
    postalCode: string | null
  }
}

export interface mediaType {
  id: string | null
  local?: {
    height: number
    width: number
    url: string | null
    localFile: {
      childImageSharp: {
        gatsbyImageData: IGatsbyImageData | null
      }
    }
    formats: {
      large: {
        url: string
      }
      small: {
        url: string
      }
      medium: {
        url: string
      }
    }
  }
  media: string | IGatsbyImageData | null
  video: boolean
}

export function graphqlFirmMapper(data: Queries.FirmQuery): Firm | null {
  if (!data) {
    return null
  }

  const { strapiAccountingFirm } = data

  let officesLength: number | null = null
  if (
    strapiAccountingFirm?.offices &&
    strapiAccountingFirm?.offices.length > 0
  ) {
    officesLength = strapiAccountingFirm?.offices.length
  }

  let offersLength: number | null = null
  if (
    data?.allStrapiAccountingOffer?.edges &&
    data.allStrapiAccountingOffer.edges.length > 0
  ) {
    offersLength = data.allStrapiAccountingOffer.edges.length
  }

  let social = null
  if (strapiAccountingFirm?.social) {
    if (
      strapiAccountingFirm?.social.facebook ||
      strapiAccountingFirm?.social.linkedin ||
      strapiAccountingFirm?.social.twitter ||
      strapiAccountingFirm?.social.youtube ||
      strapiAccountingFirm?.social.instagram
    ) {
      social = {
        facebook: strapiAccountingFirm?.social.facebook || null,
        linkedin: strapiAccountingFirm?.social.linkedin || null,
        twitter: strapiAccountingFirm?.social.twitter || null,
        youtube: strapiAccountingFirm?.social.youtube || null,
        instagram: strapiAccountingFirm?.social.instagram || null,
      }
    }
  }

  const ecoSystems = strapiAccountingFirm?.ecosystems || null
  let ecoSystemsArray = null

  if (ecoSystems && ecoSystems.length > 0) {
    ecoSystemsArray = ecoSystems.map((ecoSystem) => {
      if (ecoSystem)
        return {
          name: ecoSystem.name,
          logo:
            ecoSystem.logo?.localFile?.childImageSharp?.gatsbyImageData || null,
          logoUrl: ecoSystem.logo?.localFile?.url || null,
        }
      return null
    })
  }
  ecoSystemsArray = ecoSystemsArray
    ? ecoSystemsArray.filter((ecoSystem) => ecoSystem !== null)
    : null

  const jobTypes = strapiAccountingFirm?.jobTypes || null
  let jobTypesArray = null
  if (jobTypes) {
    jobTypesArray = jobTypes.map((jobType) => {
      if (jobType) {
        return {
          value: jobType.value,
        }
      }
      return null
    })
  }
  jobTypesArray = jobTypesArray
    ? jobTypesArray.filter((jobType) => jobType !== null)
    : null

  const offices = strapiAccountingFirm?.offices || null
  let officesArray = null
  if (offices) {
    officesArray = offices.map((office) => {
      if (office)
        return {
          uuid: office.uuid,
          name: office.name || null,
          street: office.street || null,
          postalCode: office.postalCode || null,
          cityForMap: office.city || null,
          city: office.city || null,
          latitude: office.latitude || null,
          longitude: office.longitude || null,
        }
      return null
    })
  }
  officesArray = officesArray
    ? officesArray.filter((office) => office !== null)
    : null

  const firmOffers = data.allStrapiAccountingOffer?.edges || null
  let firmOffersArray = null
  if (firmOffers) {
    firmOffersArray = firmOffers.map((offer) => {
      if (offer && offer.node) {
        return formatFirmOffer(offer.node)
      }
      return null
    })
  }
  firmOffersArray = firmOffersArray
    ? firmOffersArray.filter((offer) => offer !== null)
    : null

  const firmTeams = data.allStrapiAccountingBusinessUnit?.edges || null
  let firmTeamsArray = null
  if (firmTeams) {
    firmTeamsArray = firmTeams.map((team) => {
      if (team && team.node) {
        return {
          name: team.node.name || null,
          foundedYear: team.node.foundedYear || null,
          totalEmployees: team.node.totalEmployees || null,
          employeeAgeAverage: team.node.employeeAgeAverage || null,
          description: returnNullIfEmptyString(
            team.node.description?.data?.description,
          ),
          mainMedia: getMainMedia(
            team.node.medias as
            | {
              id: string
              url?: string | null
              local?: {
                localFile?: {
                  childImageSharp?: { gatsbyImageData?: IGatsbyImageData }
                }
                data?: { attributes?: { url: string | null } }
              }
            }[]
            | null,
          ),
          socials: {
            facebook: team.node.social?.facebook || null,
            linkedin: team.node.social?.linkedin || null,
            twitter: team.node.social?.twitter || null,
            youtube: team.node.social?.youtube || null,
            instagram: team.node.social?.instagram || null,
          },
          medias:
            team.node.medias?.map((media) => {
              if (media && media.url) {
                return {
                  id: media.id,
                  media: media.url,
                  video: true,
                }
              }
              if (media && media.local) {
                return {
                  id: media.id,
                  media:
                    media.local?.localFile?.childImageSharp?.gatsbyImageData ||
                    null,
                  video: false,
                }
              }
              return null
            }) || null,
        }
      }
      return null
    })
  }

  firmTeamsArray = firmTeamsArray
    ? firmTeamsArray.filter((team) => team !== null)
    : null

  const badges = strapiAccountingFirm?.badges || null
  let badgesArray = null
  if (badges) {
    badgesArray = badges.map((badge) => {
      if (badge)
        return {
          name: (badge.name as string) || null,
          uuid: badge.uuid || null,
          description: badge.description?.data?.description
            ? badge.description?.data?.description
            : null,
        }
      return null
    })
  }
  badgesArray = badgesArray
    ? badgesArray.filter((badge) => badge !== null)
    : null

  const tools = strapiAccountingFirm?.accountingTools || null
  let toolsArray = null
  if (tools) {
    toolsArray = tools.map((tool) => {
      if (tool) {
        const toolSlug = tool.permalink?.split('/').pop() || tool.permalink
        return {
          name: tool.name,
          logo: tool.logo?.localFile?.url || null,
          logoUrl: tool.logo?.localFile?.url || null,
          slug: toolSlug || null,
        }
      }
      return null
    })
  }

  toolsArray = toolsArray ? toolsArray.filter((tool) => tool !== null) : null

  const subscription = strapiAccountingFirm?.subscription || null

  return {
    id: strapiAccountingFirm?.strapi_id || null,
    uuid: strapiAccountingFirm?.uuid || null,
    name: strapiAccountingFirm?.name,
    slug: strapiAccountingFirm?.slug || null,
    subscription,
    totalEmployees: strapiAccountingFirm?.totalEmployees || null,
    foundedYear: strapiAccountingFirm?.foundedYear || null,
    employeeAgeAverage: strapiAccountingFirm?.employeeAgeAverage || null,
    totalClients: strapiAccountingFirm?.totalClients || null,
    colorCareerSite: strapiAccountingFirm?.colorCareerSite || null,
    hasCareerSite: strapiAccountingFirm?.hasCareerSite || null,
    premium: strapiAccountingFirm?.premium || null,
    website: strapiAccountingFirm?.website || null,
    description: returnNullIfEmptyString(
      strapiAccountingFirm?.description?.data?.description,
    ),
    introduction: returnNullIfEmptyString(
      strapiAccountingFirm?.introduction?.data?.introduction,
    ),
    firmValues: returnNullIfEmptyString(
      strapiAccountingFirm?.firmValues?.data?.firmValues,
    ),
    clientDescription:
      returnNullIfEmptyString(
        strapiAccountingFirm?.clientDescription?.data?.clientDescription,
      ) || null,
    logo:
      strapiAccountingFirm?.logo?.localFile?.childImageSharp?.gatsbyImageData ||
      null,
    logoUrl: addDomainOnUrl(strapiAccountingFirm?.logo?.url) || null,
    header:
      strapiAccountingFirm?.header?.localFile?.childImageSharp
        ?.gatsbyImageData || null,
    headerUrl: addDomainOnUrl(strapiAccountingFirm?.header?.url) || null,
    nbOffices: officesLength,
    nbOffers: offersLength,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mainMedia: getMainMedia(strapiAccountingFirm?.medias as any),
    badges: badgesArray,
    ecoSystems: ecoSystemsArray,
    jobTypes: jobTypesArray,
    socials: social,
    medias:
      strapiAccountingFirm?.medias?.map(formatMediaFromGraphqlRequest) || null,
    tools: toolsArray,
    offices: officesArray,
    offers: firmOffersArray,
    teams: firmTeamsArray,
    careerSite: careerSiteInfos(strapiAccountingFirm as unknown as Firm),
  }
}

function formatFirmOffer(firmOffer: {
  title: string | null
  slug: string
  publishedAt: Date | string | null
  jobType: JobType
  payRange: {
    min: number | null
    max: number | null
  } | null
  contractType: string | null
  startDate: string | null
  office: {
    street: string | null
    city: string | null
    postalCode: string | null
  } | null
}): FirmOffer {
  const contractType =
    typeof firmOffer.contractType === 'string' ? firmOffer.contractType : ''
  const contractTypeFormatted = formatContractType(contractType) ?? null
  const min = (firmOffer.payRange?.min as number) ?? null
  const max = (firmOffer.payRange?.max as number) ?? null
  const cityName = firmOffer.office?.city ?? ''
  const formatedCity = `${cityName} ${firmOffer.office?.postalCode
      ? `(${firmOffer.office?.postalCode.slice(0, 2)})`
      : ''
    }`
  const startDate = firmOffer.startDate ?? null
  const startDateFormatted = formatStartDate(startDate)

  return {
    title: firmOffer.title || 'Offre sans titre',
    slug: firmOffer.slug,
    jobType: firmOffer.jobType || null,
    publishedAt: firmOffer.publishedAt || null,
    payRange: { min, max },
    contractType: contractTypeFormatted || null,
    startDate: startDateFormatted || null,
    office: {
      city: cityName,
      cityForMap: cityName,
      street: firmOffer.office?.street || null,
      postalCode: firmOffer.office?.postalCode || null,
      localisation: formatedCity,
    },
  }
}

function getMainMedia(
  medias:
    | {
      id: string | null
      url?: string | null
      local?: {
        localFile?: {
          childImageSharp?: { gatsbyImageData?: IGatsbyImageData }
        }
        data?: { attributes?: { url: string | null } }
      }
    }[]
    | null,
): mediaType | null {
  if (medias && medias.length > 0) {
    const mainMedia = medias[0]
    if (mainMedia && mainMedia.url) {
      return {
        id: mainMedia.id,
        media: mainMedia.url,
        video: true,
      }
    }
    if (mainMedia && mainMedia.local?.data?.attributes) {
      return {
        id: mainMedia.id,
        media: addDomainOnUrl(mainMedia.local?.data?.attributes?.url) || null,
        video: false,
      }
    }
    if (mainMedia && mainMedia.local?.localFile) {
      return {
        id: mainMedia.id,
        media:
          mainMedia.local?.localFile?.childImageSharp?.gatsbyImageData || null,
        video: false,
      }
    }
  }
  return null
}

function getOffersFromOffice(office: FetchOffice): FirmOffer[] {
  const offers: FirmOffer[] = []
  if (
    office.attributes.offers &&
    office.attributes.offers.data &&
    office.attributes.offers.data.length > 0
  ) {
    office.attributes.offers.data.forEach((offer) => {
      if (
        offer &&
        offer.attributes &&
        offer.attributes.positionFilled === false
      ) {
        const modifiedOffer = {
          ...offer.attributes,
          office: {
            cityForMap: office.attributes.city || null,
            street: office.attributes.street || null,
            city: office.attributes.city || null,
            postalCode: office.attributes.postalCode || null,
          },
        }

        offers.push(formatFirmOffer(modifiedOffer))
      }
    })
  }
  return offers
}

export type FetchFirm = {
  id: number
  attributes: {
    uuid: string | null
    name: string | null
    slug: string | null
    totalEmployees: string | null
    foundedYear: number | null
    employeeAgeAverage: number | null
    totalClients: number | null
    colorCareerSite: string | null
    hasCareerSite: boolean | null
    premium: boolean | null
    website: string | null
    description: string | null
    firmValues: string | null
    clientDescription: string | null
    createdAt: string | null
    updatedAt: string | null
    logo: {
      data: {
        attributes: {
          url: string | null
        }
      }
    } | null
    header: {
      data: {
        attributes: {
          url: string | null
        }
      }
    } | null
    offices: {
      data: FetchOffice[] | null
    } | null
    medias:
    | {
      id: string | null
      url: string | null
      local: {
        data: {
          attributes: {
            url: string | null
          }
        }
      }
    }[]
    | null
    social: {
      facebook: string | null
      linkedin: string | null
      twitter: string | null
      youtube: string | null
      instagram: string | null
    } | null
    ecosystems: FetchEcosystem[] | null[] | null
    accountingTools: {
      data: FetchTool[]
    } | null
    badges: {
      data: FetchBadge[]
    } | null
    jobTypes: FetchJobType[] | null
    businessUnits: {
      data: FetchBusinessUnit[]
    } | null
  }
}

export type FetchLogo = {
  data: {
    id: number
    attributes: {
      url: string | null
    }
  } | null
}

export type FetchBadge = {
  attributes: {
    name: string | null
    uuid: string | null
  }
}

export type FetchEcosystem = {
  name: string | null
  logo: FetchLogo | null
} | null

export type FetchTool = {
  attributes: {
    name: string | null
    logo: FetchLogo | null
    permalink: string | null
  }
}

export type FetchJobType = {
  id: number
  value: string
} | null

export type FetchOffice = {
  id: number
  attributes: {
    uuid: string
    name: string | null
    street: string | null
    postalCode: string | null
    city: string | null
    latitude: number | null
    longitude: number | null
    createdAt: string | null
    updatedAt: string | null
    offers: {
      data: {
        id: number
        attributes: {
          title: string
          slug: string | null
          jobType: {
            value: string | null
          } | null
          publishedAt: string | null
          fullyRemoteJob: boolean | null
          positionFilled: boolean | null
          hoursPerWeek: number | null
          educationRequirement: string | null
          experienceRequirement: string | null
          createdAt: string | null
          updatedAt: string | null
          payRange: {
            min: number | null
            max: number | null
          } | null
          contractType: string | null
          startDate: string | null
        }
      }[]
    } | null
  }
}

export type FetchBusinessUnit = {
  attributes: {
    name: string | null
    foundedYear: number | null
    totalEmployees: string | null
    employeeAgeAverage: number | null
    description: string | null
    social: {
      facebook: string | null
      linkedin: string | null
      twitter: string | null
      youtube: string | null
      instagram: string | null
    }
    medias:
    | {
      id: string | null
      url?: string
      local?: {
        localFile?: {
          childImageSharp?: {
            gatsbyImageData?: IGatsbyImageData
          }
        }
        data?: {
          attributes?: {
            url: string
          }
        }
      }
    }[]
    | null
  }
}

export function fetchFirmMapper(data: FetchFirm): Firm | null {
  let firm: Firm | null = null

  let officesLength: number | null = null
  let offersLength: number | null = null
  const firmOffers: FirmOffer[] = []

  if (
    data?.attributes.offices?.data &&
    data.attributes?.offices?.data.length > 0
  ) {
    officesLength = data.attributes.offices.data.length
    const offices = data.attributes.offices.data

    offices.forEach((office) => {
      if (
        office &&
        office.attributes.offers &&
        office.attributes.offers.data &&
        office.attributes.offers.data.length > 0
      ) {
        const offers = getOffersFromOffice(office)
        firmOffers.push(...offers)
        offersLength = firmOffers.length
      }
    })
  }

  firmOffers.sort((a, b) => {
    if (a?.publishedAt && b?.publishedAt) {
      const aTime = new Date(a.publishedAt).getTime()
      const bTime = new Date(b.publishedAt).getTime()
      return bTime - aTime
    }
    return 0
  })

  let social = null

  if (data.attributes.social) {
    social = {
      facebook: data.attributes.social.facebook || null,
      linkedin: data.attributes.social.linkedin || null,
      twitter: data.attributes.social.twitter || null,
      youtube: data.attributes.social.youtube || null,
      instagram: data.attributes.social.instagram || null,
    }
  }

  const jobTypes = data.attributes?.jobTypes || null
  let jobTypesArray = null
  if (jobTypes) {
    jobTypesArray = jobTypes.map((jobType) => {
      if (jobType) {
        return {
          value: jobType.value,
        }
      }
      return null
    })
  }
  jobTypesArray = jobTypesArray
    ? jobTypesArray.filter((jobType) => jobType !== null)
    : null

  const ecoSystems = data.attributes?.ecosystems || null
  let ecoSystemsArray = null
  if (ecoSystems && ecoSystems.length > 0) {
    ecoSystemsArray = ecoSystems.map((ecoSystem) => {
      if (ecoSystem) {
        return {
          name: ecoSystem.name || null,
          logo: ecoSystem.logo?.data?.attributes?.url || null,
          logoUrl: ecoSystem.logo?.data?.attributes?.url || null,
        }
      }
      return null
    })
  }
  ecoSystemsArray = ecoSystemsArray
    ? ecoSystemsArray.filter((ecoSystem) => ecoSystem !== null)
    : null

  const tools = data.attributes?.accountingTools || null
  let toolsArray = null
  if (tools) {
    toolsArray = tools.data.map((tool) => {
      if (tool) {
        const toolSlug =
          tool.attributes.permalink?.split('/').pop() ||
          tool.attributes.permalink
        return {
          name: tool.attributes.name || null,
          logo:
            addDomainOnUrl(tool.attributes.logo?.data?.attributes?.url) || null,
          logoUrl:
            addDomainOnUrl(tool.attributes.logo?.data?.attributes?.url) || null,
          slug: toolSlug || null,
        }
      }
      return null
    })
  }
  toolsArray = toolsArray ? toolsArray.filter((tool) => tool !== null) : null

  const offices = data.attributes?.offices || null
  let officesArray = null
  if (offices && offices.data) {
    officesArray = offices.data.map((office) => {
      if (office) {
        return {
          uuid: office.attributes.uuid,
          name: office.attributes.name || null,
          postalCode: office.attributes.postalCode || null,
          cityForMap: office.attributes.city || null,
          city: office.attributes.city || null,
          street: office.attributes.street || null,
          latitude: office.attributes.latitude || null,
          longitude: office.attributes.longitude || null,
        }
      }
      return null
    })
  }
  officesArray = officesArray
    ? officesArray.filter((office) => office !== null)
    : null

  if (data.attributes) {
    firm = {
      name: data.attributes.name || null,
      slug: data.attributes.slug || null,
      totalEmployees: data.attributes.totalEmployees || null,
      foundedYear: data.attributes.foundedYear || null,
      employeeAgeAverage: data.attributes.employeeAgeAverage || null,
      totalClients: data.attributes.totalClients || null,
      colorCareerSite: data.attributes.colorCareerSite || null,
      hasCareerSite: data.attributes.hasCareerSite || null,
      premium: data.attributes.premium || null,
      website: data.attributes.website || null,
      description: data.attributes.description || null,
      firmValues: data.attributes.firmValues || null,
      clientDescription: data.attributes.clientDescription || null,
      logo: addDomainOnUrl(data.attributes.logo?.data?.attributes?.url) || null,
      logoUrl:
        addDomainOnUrl(data.attributes.logo?.data?.attributes?.url) || null,
      header:
        addDomainOnUrl(data.attributes.header?.data?.attributes?.url) || null,
      headerUrl:
        addDomainOnUrl(data.attributes.header?.data?.attributes?.url) || null,
      nbOffices: officesLength,
      nbOffers: offersLength,
      mainMedia: getMainMedia(data.attributes.medias || null),
      badges:
        data.attributes.badges?.data?.map((badge: FetchBadge) => {
          if (badge && badge.attributes) {
            return {
              name: badge.attributes.name || null,
              uuid: badge.attributes?.uuid || null,
            }
          }
          return null
        }) || null,
      ecoSystems: ecoSystemsArray,
      jobTypes: jobTypesArray,
      socials: social,
      medias:
        data.attributes.medias?.map(
          (mediaData: {
            id: string | null
            url?: string | null
            local?: {
              data?: {
                attributes?: {
                  url?: string | null
                }
              }
              localFile?: {
                childImageSharp?: {
                  gatsbyImageData?: IGatsbyImageData
                }
              }
            }
          }) => {
            if (mediaData && mediaData.url) {
              return {
                id: mediaData.id,
                media: mediaData.url,
                video: true,
              }
            }
            if (mediaData && mediaData.local?.data?.attributes) {
              return {
                id: mediaData.id,
                media:
                  addDomainOnUrl(
                    mediaData.local?.data?.attributes?.url ?? undefined,
                  ) || null,
                video: false,
              }
            }
            if (mediaData && mediaData.local?.localFile) {
              return {
                id: mediaData.id,
                media:
                  mediaData.local?.localFile?.childImageSharp
                    ?.gatsbyImageData || null,
                video: false,
              }
            }
            return null
          },
        ) || null,
      tools: toolsArray,
      offices: officesArray,
      offers: firmOffers,
      teams:
        data.attributes.businessUnits?.data?.map((team: FetchBusinessUnit) => {
          if (team && team.attributes) {
            return {
              name: team.attributes.name || null,
              foundedYear: team.attributes.foundedYear || null,
              totalEmployees: team.attributes.totalEmployees || null,
              employeeAgeAverage: team.attributes.employeeAgeAverage || null,
              description: team.attributes.description || null,
              mainMedia: getMainMedia(team.attributes.medias || null),
              socials: {
                facebook: team.attributes.social.facebook || null,
                linkedin: team.attributes.social.linkedin || null,
                twitter: team.attributes.social.twitter || null,
                youtube: team.attributes.social.youtube || null,
                instagram: team.attributes.social.instagram || null,
              },
              medias:
                team.attributes.medias?.map((mediaData) => {
                  if (mediaData && mediaData.url) {
                    return {
                      id: mediaData.id,
                      media: mediaData.url,
                      video: true,
                    }
                  }
                  if (mediaData && mediaData.local?.data?.attributes) {
                    return {
                      id: mediaData.id,
                      media:
                        addDomainOnUrl(
                          mediaData.local?.data?.attributes?.url,
                        ) || null,
                      video: false,
                    }
                  }
                  if (mediaData && mediaData.local?.localFile) {
                    return {
                      id: mediaData.id,
                      media:
                        mediaData.local?.localFile?.childImageSharp
                          ?.gatsbyImageData || null,
                      video: false,
                    }
                  }
                  return null
                }) || null,
            }
          }
          return null
        }) || null,
    }
    return firm
  }
  return null
}
